import { ReactNode } from "react";
import { MobileBottomNav } from "./MobileBottomNav";
import { Button } from "@/components/ui/button";
import { Bell, Download, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface MobileLayoutProps {
  children: ReactNode;
}

export function MobileLayout({ children }: MobileLayoutProps) {
  const { user } = useAuth();
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const [showInstallBanner, setShowInstallBanner] = useState(true);
  const navigate = useNavigate();
  
  const getInitials = (email: string | undefined) => {
    if (!email) return "?";
    return email.substring(0, 2).toUpperCase();
  };

  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      setShowInstallBanner(false);
    } else {
      // If prompt doesn't work, navigate to install page
      navigate("/install");
    }
  };

  return (
    <div className="flex flex-col min-h-screen w-full max-w-full overflow-x-hidden pb-safe">
      {/* Mobile Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b px-4 py-3">
        <div className="flex items-center justify-between max-w-full">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-white font-bold text-sm">RG</span>
            </div>
            <div>
              <div className="text-sm font-bold">RoofGenius</div>
              <div className="text-xs text-muted-foreground">AI</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-9 w-9">
              <Bell className="h-5 w-5" />
            </Button>
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-xs">
                {getInitials(user?.email)}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </header>

      {/* Install Banner */}
      {isInstallable && !isInstalled && showInstallBanner && (
        <div className="sticky top-[57px] z-40 bg-primary/95 backdrop-blur text-primary-foreground px-4 py-2 border-b border-primary/20">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Download className="h-4 w-4 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium">Install RoofGenius AI</div>
                <div className="text-xs opacity-90">Get faster access & offline mode</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button 
                onClick={handleInstall}
                size="sm" 
                variant="secondary"
                className="h-7 text-xs px-2"
              >
                Install
              </Button>
              <Button 
                onClick={() => setShowInstallBanner(false)}
                size="icon" 
                variant="ghost"
                className="h-7 w-7"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content with Safe Area Padding */}
      <main className="flex-1 w-full max-w-full overflow-y-auto overflow-x-hidden pb-32">
        <div className="w-full max-w-full">
          {children}
        </div>
      </main>

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
